import { ArrowRight, CheckCircle2, CircleAlert, Minus, Plus, RefreshCw } from 'lucide-react';
import {
  buildRouteDiff,
  routeSnapshotChain,
  type RouteDiffChange,
  type RouteDiffField,
} from '../RouteDiff';
import type { TelephonyRoutePreview, TelephonyRoutingSnapshot } from '../types';

type RoutingDiffViewProps = {
  preview: TelephonyRoutePreview;
  memberNames: ReadonlyMap<string, string>;
  compact?: boolean;
  historical?: boolean;
  beforeTitle?: string;
  afterTitle?: string;
};

export function RoutingDiffView(_props: RoutingDiffViewProps) {
  const {
    preview,
    memberNames,
    compact = false,
    historical = false,
    beforeTitle = historical ? 'Исходная схема' : 'Работает сейчас',
    afterTitle = historical ? 'Зафиксированная схема' : 'Будет применено',
  } = _props;
  const diff = buildRouteDiff(preview.appliedSnapshot, preview.snapshot);
  const allGroups = new Map([
    ...(preview.appliedSnapshot?.groups || []).map(group => [group.id, group] as const),
    ...preview.snapshot.groups.map(group => [group.id, group] as const),
  ]);
  const groupName = (id: string | null) => id ? allGroups.get(id)?.name || 'Неизвестная группа' : 'Нет';
  const memberName = (id: string) => memberNames.get(id) || `Сотрудник ${id.slice(0, 8)}`;

  const value = (field: RouteDiffField, raw: string | number | boolean | null) => {
    if (field === 'strategy') return raw === 'SEQUENTIAL' ? 'Последовательно' : 'Всем одновременно';
    if (field === 'ringSeconds' || field === 'maxWaitSeconds') return `${raw ?? 0} сек.`;
    if (field === 'fallbackQueueId') return groupName(typeof raw === 'string' ? raw : null);
    if (field === 'isActive') return raw ? 'Включена' : 'Отключена';
    return String(raw ?? '—');
  };

  const fieldLabel: Record<RouteDiffField, string> = {
    name: 'Название',
    strategy: 'Стратегия вызова',
    ringSeconds: 'Время звонка сотруднику',
    maxWaitSeconds: 'Общий тайм-аут',
    fallbackQueueId: 'Резервная группа',
    isActive: 'Состояние группы',
  };

  const changeRow = (change: RouteDiffChange, index: number) => {
    if (change.kind === 'FIELD') {
      return <div className="telephony-route-diff-change" key={`${change.field}-${index}`}>
        <span>{fieldLabel[change.field]}</span>
        <del>{value(change.field, change.before)}</del>
        <ArrowRight />
        <ins>{value(change.field, change.after)}</ins>
      </div>;
    }
    if (change.kind === 'MEMBER_ADDED') {
      return <div className="telephony-route-diff-change member added" key={`${change.kind}-${change.memberId}`}>
        <span>Добавлен сотрудник</span><ins>{memberName(change.memberId)} · №{change.afterPosition}</ins>
      </div>;
    }
    if (change.kind === 'MEMBER_REMOVED') {
      return <div className="telephony-route-diff-change member removed" key={`${change.kind}-${change.memberId}`}>
        <span>Удалён сотрудник</span><del>{memberName(change.memberId)} · №{change.beforePosition}</del>
      </div>;
    }
    return <div className="telephony-route-diff-change member moved" key={`${change.kind}-${change.memberId}`}>
      <span>Порядок сотрудника</span>
      <b>{memberName(change.memberId)}</b>
      <del>№{change.beforePosition}</del>
      <ArrowRight />
      <ins>№{change.afterPosition}</ins>
    </div>;
  };

  return <div className={`telephony-route-diff ${compact ? 'compact' : ''} ${historical ? 'historical' : ''}`}>
    <div className="telephony-route-diff-snapshots">
      <RouteSnapshotCard
        title={beforeTitle}
        revision={preview.appliedRevision}
        snapshot={preview.appliedSnapshot}
        legacyLabel={historical ? 'Снимок исходной схемы отсутствует' : 'Стабильная legacy-схема'}
      />
      <ArrowRight className="telephony-route-diff-arrow" />
      <RouteSnapshotCard
        title={afterTitle}
        revision={preview.draftRevision}
        snapshot={preview.snapshot}
        targetCount={historical ? undefined : preview.technical?.bridge.targetCount}
      />
    </div>

    {diff.mode === 'UNCHANGED' && <div className="telephony-route-diff-state unchanged">
      <CheckCircle2 />
      <span><b>Изменений нет</b><small>{historical ? 'Зафиксированные снимки полностью совпадают.' : 'Черновик полностью совпадает с применённой схемой.'}</small></span>
    </div>}
    {diff.mode === 'INITIAL' && <div className="telephony-route-diff-state initial">
      <CircleAlert />
      <span><b>{historical ? 'Первая зафиксированная управляемая схема' : 'Первое применение управляемого маршрута'}</b><small>{historical ? 'До этой операции управляемый снимок маршрута отсутствовал.' : 'Текущая legacy-схема будет заменена показанным черновиком.'}</small></span>
    </div>}
    {diff.mode === 'CHANGED' && <div className="telephony-route-diff-state changed">
      <RefreshCw />
      <span><b>{diff.totalChanges} {changeCountLabel(diff.totalChanges)}</b><small>{historical ? 'Зафиксированные изменения рабочего маршрута.' : 'Ниже перечислено всё, что изменится в рабочем маршруте.'}</small></span>
    </div>}

    {diff.totalChanges > 0 && <div className="telephony-route-diff-list">
      <div className="telephony-route-diff-list-head"><span>ИЗМЕНЁННЫЕ ЭЛЕМЕНТЫ</span><b>{diff.totalChanges}</b></div>
      {diff.root && <article className="changed root">
        <header><RefreshCw /><span><b>Основная входящая группа</b><small>Начальная точка маршрута</small></span></header>
        <div className="telephony-route-diff-change">
          <span>Основная группа</span>
          <del>{diff.root.beforeId ? groupName(diff.root.beforeId) : 'Legacy-схема'}</del>
          <ArrowRight />
          <ins>{groupName(diff.root.afterId)}</ins>
        </div>
      </article>}
      {diff.groups.map(group => <article className={group.status.toLowerCase()} key={`${group.status}-${group.id}`}>
        <header>
          {group.status === 'ADDED' ? <Plus /> : group.status === 'REMOVED' ? <Minus /> : <RefreshCw />}
          <span>
            <b>{group.name}</b>
            <small>{group.status === 'ADDED' ? 'Новая группа' : group.status === 'REMOVED' ? historical ? 'Группа удалена' : 'Группа будет удалена' : `${group.changes.length} ${changeCountLabel(group.changes.length)}`}</small>
          </span>
          <em>{group.status === 'ADDED' ? historical ? 'Добавлено' : 'Добавится' : group.status === 'REMOVED' ? historical ? 'Удалено' : 'Удалится' : historical ? 'Изменено' : 'Изменится'}</em>
        </header>
        {group.changes.map(changeRow)}
      </article>)}
    </div>}
  </div>;
}

function strategyLabel(strategy: TelephonyRoutingSnapshot['groups'][number]['strategy']) {
  return strategy === 'SEQUENTIAL' ? 'Последовательно' : 'Всем одновременно';
}

function changeCountLabel(count: number) {
  return russianCountLabel(count, 'изменение', 'изменения', 'изменений');
}

function russianCountLabel(count: number, one: string, few: string, many: string) {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

function RouteSnapshotCard({
  title,
  revision,
  snapshot,
  legacyLabel,
  targetCount,
}: {
  title: string;
  revision: number;
  snapshot: TelephonyRoutingSnapshot | null;
  legacyLabel?: string;
  targetCount?: number;
}) {
  if (!snapshot) {
    return <section className="telephony-route-snapshot-card legacy">
      <header><span>{title}</span><em>legacy</em></header>
      <div className="telephony-route-snapshot-empty"><b>{legacyLabel || 'Нет применённой схемы'}</b><small>Управляемый снимок ещё не зафиксирован.</small></div>
    </section>;
  }

  const chain = routeSnapshotChain(snapshot);
  const root = snapshot.groups.find(group => group.id === snapshot.rootQueueId);
  const memberCount = new Set(snapshot.groups.flatMap(group => group.members.map(member => member.telephonyUserId))).size;
  return <section className="telephony-route-snapshot-card">
    <header><span>{title}</span><em>r{revision}</em></header>
    <div className="telephony-route-snapshot-root">
      <small>ОСНОВНАЯ ГРУППА</small>
      <b>{root?.name || 'Не определена'}</b>
      <span>
        {snapshot.groups.length} {russianCountLabel(snapshot.groups.length, 'группа', 'группы', 'групп')}
        {' · '}
        {memberCount} {russianCountLabel(memberCount, 'сотрудник', 'сотрудника', 'сотрудников')}
      </span>
    </div>
    {typeof targetCount === 'number' && <div className="telephony-route-snapshot-targets">
      <span><small>ДОСТУПНО ДЛЯ ЗВОНКА</small><b>{targetCount}</b></span>
      <em>Рассчитано для FreeSWITCH</em>
    </div>}
    <div className="telephony-route-snapshot-chain">
      {chain.map((group, index) => <div key={group.id} className={!group.isActive ? 'disabled' : ''}>
        <i>{index + 1}</i>
        <span>
          <b>{group.name}</b>
          <small>
            {strategyLabel(group.strategy)} · {group.members.length}{' '}
            {russianCountLabel(group.members.length, 'участник', 'участника', 'участников')}
          </small>
        </span>
      </div>)}
      {!chain.length && <small>Цепочка маршрута не определена.</small>}
    </div>
  </section>;
}
